/*
 * Snooze's choices (master-inbox plan, step 3b): Later today, Tomorrow morning, Next week, each with
 * the time it means beside it, and Pick a date and time with the app's own date-and-time field
 * (`date-time-fields.tsx`). A conversation already asleep can be woken here too. Drawn as the
 * option menu's popover is (`.lcr-popover`, `.lcr-menu-item`); arrows move between the choices and
 * Escape closes it.
 */
import { useEffect, useId, useRef, useState } from "react";
import { appTime, appWeekdayTime, viewerTimeZone } from "../../src/app-dates.ts";
import type { InboxConversation } from "../../src/inbox-v2/contract.ts";
import { DateTimeField } from "../date-time-fields.tsx";
import { snoozePresets, wallInstant } from "./snooze-times.ts";
import type { InboxPageModel, SnoozeAnchor } from "./use-inbox-page.ts";

/** Where a key moves among the menu's buttons; undefined for any other key. */
function moved(items: HTMLElement[], key: string): HTMLElement | undefined {
  const active = document.activeElement;
  const index = active instanceof HTMLElement ? items.indexOf(active) : -1;
  if (key === "ArrowDown") return items[(index + 1) % items.length];
  if (key === "ArrowUp") return items[(index - 1 + items.length) % items.length];
  return undefined;
}

/** What snoozing does to Done, said only where it applies (the API reopens a Done conversation). */
function doneNote(rows: readonly InboxConversation[]): string | null {
  const done = rows.filter((row) => row.doneAt !== null).length;
  if (done === 0) return null;
  return done === 1 && rows.length === 1
    ? "Snoozing takes this out of Done. It returns to the inbox when it wakes."
    : `Snoozing takes ${done === rows.length ? "them" : `the ${done} marked done`} out of Done. They return to the inbox when they wake.`;
}

function PickTime(props: { onSnooze: (until: string) => void; onBack: () => void }) {
  const id = useId();
  const zone = viewerTimeZone();
  const [value, setValue] = useState("");
  const at = value ? wallInstant(value, zone) : null;
  return (
    <form
      className="lcr-snooze-pick"
      onSubmit={(event) => {
        event.preventDefault();
        if (at !== null) props.onSnooze(new Date(at).toISOString());
      }}
    >
      <label className="lcr-settings-field-label" htmlFor={id}>
        Date and time
      </label>
      <DateTimeField id={id} value={value} onChange={setValue} zone={zone} notPast autoFocus />
      <span className="lcr-snooze-pick-actions">
        <button type="button" className="lcr-small-button lcr-ghost" onClick={props.onBack}>
          Back
        </button>
        <button type="submit" className="lcr-small-button" disabled={at === null}>
          Snooze
        </button>
      </span>
    </form>
  );
}

function SnoozeMenu(props: {
  rows: readonly InboxConversation[];
  onSnooze: (until: string) => void;
  onUnsnooze: () => void;
  onClose: () => void;
  /** The instant the presets are read from; tests pin it. */
  now?: number;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [picking, setPicking] = useState(false);
  const presets = snoozePresets(props.now ?? Date.now());
  const asleep = props.rows.some((row) => row.snooze);
  const note = doneNote(props.rows);
  const close = useRef(props.onClose);
  close.current = props.onClose;
  // Read while rendering, before the first choice takes focus: what opened the menu.
  const [opened] = useState(() => document.activeElement);
  useEffect(() => {
    if (!picking) root.current?.querySelector<HTMLElement>(".lcr-menu-item")?.focus();
  }, [picking]);
  useEffect(() => {
    // Focus goes back where it came from: a row's own Snooze hides again, so its row takes it.
    const opener = opened?.closest(".lcr-row-wrap")?.querySelector(".lcr-row") ?? opened;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && root.current?.contains(event.target)) return;
      // A date field's own calendar opens outside this box; a press there is still inside.
      if (event.target instanceof Element && event.target.closest(".lcr-entry-dialog")) return;
      close.current();
    };
    document.addEventListener("pointerdown", outside);
    return () => {
      document.removeEventListener("pointerdown", outside);
      if (opener instanceof HTMLElement && opener !== document.body && opener.isConnected)
        opener.focus({ preventScroll: true });
    };
  }, [opened]);
  return (
    <div
      ref={root}
      className="lcr-popover lcr-snooze-menu"
      role="dialog"
      aria-label="Snooze"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          return props.onClose();
        }
        const items = [...(root.current?.querySelectorAll<HTMLElement>(".lcr-menu-item") ?? [])];
        const target = picking ? undefined : moved(items, event.key);
        if (!target) return;
        event.preventDefault();
        target.focus();
      }}
    >
      <p className="lcr-label">Snooze until</p>
      {picking ? (
        <PickTime onSnooze={props.onSnooze} onBack={() => setPicking(false)} />
      ) : (
        <>
          {asleep && (
            <button type="button" className="lcr-menu-item" onClick={props.onUnsnooze}>
              <span className="lcr-menu-item-label">Unsnooze now</span>
              <span className="lcr-snooze-when">Back to the inbox</span>
            </button>
          )}
          {presets.map((preset) => (
            <button
              type="button"
              key={preset.id}
              className="lcr-menu-item"
              onClick={() => props.onSnooze(preset.until)}
            >
              <span className="lcr-menu-item-label">{preset.label}</span>
              <span className="lcr-snooze-when">
                {preset.id === "later" ? appTime(preset.until) : appWeekdayTime(preset.until)}
              </span>
            </button>
          ))}
          <button type="button" className="lcr-menu-item" onClick={() => setPicking(true)}>
            <span className="lcr-menu-item-label">Pick a date and time</span>
          </button>
        </>
      )}
      {note && <p className="lcr-snooze-note">{note}</p>}
    </div>
  );
}

/**
 * Snooze's choices, open where `anchor` says, for the rows they were opened for. One menu serves the
 * list's row, the bulk bar, H and the open conversation's head, so the presets and Undo are one.
 */
export function snoozeMenuAt(page: InboxPageModel, anchor: SnoozeAnchor) {
  const open = page.snoozing;
  const here =
    open !== null &&
    (typeof anchor === "string"
      ? open.anchor === anchor
      : typeof open.anchor === "object" && open.anchor.row === anchor.row);
  if (!here) return null;
  return (
    <SnoozeMenu
      rows={open.rows}
      onSnooze={(until) => void page.snooze(until)}
      onUnsnooze={() => void page.unsnooze()}
      onClose={page.closeSnooze}
    />
  );
}
